import type { ReactNode } from "react";
import { Box, Skeleton, Stack } from "@mui/material";

type CardKind = "course" | "unit";

function CardSkeleton({ kind }: { kind: CardKind }) {
  return (
    <Box
      sx={{
        height: "100%",
        boxSizing: "border-box",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {kind === "course" && (
        <Skeleton variant="rectangular" height={128} sx={{ borderRadius: 0 }} />
      )}
      <Stack spacing={1.5} sx={{ p: 2.5 }}>
        <Skeleton width="40%" height={24} />
        <Skeleton width="75%" height={32} />
        <Skeleton width="100%" height={24} />
        <Skeleton width="65%" height={24} />
        <Skeleton variant="rounded" height={5} sx={{ mt: 2 }} />
        {kind === "unit" && <Skeleton variant="rounded" height={42} />}
      </Stack>
    </Box>
  );
}

/** Mount covers immediately, but reveal the entire card only when its data is ready. */
export function CatalogCardReveal({
  pending,
  kind,
  children,
}: {
  pending: boolean;
  kind: CardKind;
  children: ReactNode;
}) {
  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {pending && (
        <Box
          role="status"
          aria-label={`Loading ${kind}`}
          aria-busy="true"
          sx={{ position: "absolute", inset: 0, overflow: "hidden" }}
        >
          <Box aria-hidden="true" sx={{ height: "100%" }}>
            <CardSkeleton kind={kind} />
          </Box>
        </Box>
      )}
      <Box
        aria-hidden={pending || undefined}
        sx={{ height: "100%", visibility: pending ? "hidden" : "visible" }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function CatalogSkeleton({
  kind = "course",
  count = 2,
}: {
  kind?: "course" | "unit";
  count?: number;
}) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: 4,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <CardSkeleton key={index} kind={kind} />
      ))}
    </Box>
  );
}
